import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Copy, Check, ExternalLink, Wallet } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import PayPalButton from '../../components/paypal/PayPalButton';

// PayPal Button IDs for each tier
const PAYPAL_BUTTONS = {
  starter: '8ZHXMW39M3EVA',
  professional: 'QEDYHET744S8A',
  enterprise: '7YGUFSBWBCEK4'
};

const PAYPAL_CLIENT_ID = 'BAAie7-_LGGsVivHXw98Y7puC1wtLO2xkBJwkn9PU0ib2iVyPGzn6o2NiEVy5fPyGdiNyS82VYUWH7EoCI';

const SERVICES = {
  // Subscription Tiers
  starter: { name: 'Starter', price: 99, priceCrypto: 91, billing: '/month' },
  professional: { name: 'Professional', price: 299, priceCrypto: 275, billing: '/month' },
  enterprise: { name: 'Enterprise', price: 999, priceCrypto: 919, billing: '/month' },
  
  // Add-ons
  certification: { name: 'CorpCraft Certification', price: 99, priceCrypto: 91 },
  blockchain_anchor: { name: 'Blockchain Anchor', price: 29, priceCrypto: 27 },
  email_signing_pack: { name: 'Email Signing Pack', price: 49, priceCrypto: 45 },
  team_seat: { name: 'Team Seat', price: 25, priceCrypto: 23, billing: '/month' },
  trademark_filing: { name: 'USPTO Trademark Filing', price: 500, priceCrypto: 460 },
  trademark_deep_scan: { name: 'Deep-Scan Trademark Clearance', price: 49, priceCrypto: 45 }
};

export default function CheckoutPage() {
  const { serviceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Check if it's an add-on (crypto only)
  const isAddon = !['starter', 'professional', 'enterprise'].includes(serviceId);
  
  const [paymentMethod, setPaymentMethod] = useState(
    isAddon ? 'crypto' : (location.state?.paymentMethod || 'paypal')
  );
  const [loading, setLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [txHash, setTxHash] = useState('');
  const [copied, setCopied] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);

  const service = SERVICES[serviceId];
  const displayPrice = paymentMethod === 'crypto' ? service?.priceCrypto : service?.price;

  // Platform USDC receiving address (from backend)
  const PLATFORM_WALLET = '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb';  // Replace with your wallet
  const USDC_POLYGON = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';

  useEffect(() => {
    if (!service) {
      navigate('/pricing');
    }
  }, [service, navigate]);

  // Load PayPal SDK
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalLoaded && !window.paypal) {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=hosted-buttons&enable-funding=venmo&currency=USD`;
      script.async = true;
      script.onload = () => setPaypalLoaded(true);
      document.body.appendChild(script);
    } else if (window.paypal && !paypalLoaded) {
      setPaypalLoaded(true);
    }
  }, [paymentMethod, paypalLoaded]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCryptoSubmit = async (e) => {
    e.preventDefault();
    
    if (!txHash || txHash.length < 60) {
      toast.error('Please enter a valid transaction hash');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/payments/submit-crypto-payment', {
        service_id: serviceId,
        tx_hash: txHash,
        wallet_address: walletAddress,
        amount_usdc: displayPrice,
        filing_id: location.state?.filing_id
      });

      toast.success('Payment submitted! We will verify and activate your service within 1 hour.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setLoading(false);
    }
  };

  if (!service) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Checkout</h1>
            <p className="text-slate-400">Complete your purchase of {service.name}</p>
          </div>

          {/* Order Summary */}
          <div className="bg-slate-900/50 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-300">{service.name}</span>
              <span className="font-bold">
                ${displayPrice} {paymentMethod === 'crypto' ? 'USDC' : 'USD'}
                {service.billing && <span className="text-sm text-slate-400">{service.billing}</span>}
              </span>
            </div>
            {paymentMethod === 'crypto' && service.price !== service.priceCrypto && (
              <div className="text-sm text-green-400 text-right">
                You save ${service.price - service.priceCrypto} (8% off)
              </div>
            )}
          </div>

          {/* Payment Method Toggle */}
          {!isAddon && (
            <div className="mb-8">
              <Label className="mb-3 block">Payment Method</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentMethod === 'paypal'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="text-3xl mb-2">💙</div>
                  <div className="font-semibold">PayPal</div>
                  <div className="text-sm text-slate-400">Secure & Fast</div>
                </button>
                <button
                  onClick={() => setPaymentMethod('crypto')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    paymentMethod === 'crypto'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <Wallet className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">Crypto (USDC)</div>
                  <div className="text-sm text-green-400">Save 8%</div>
                </button>
              </div>
            </div>
          )}

          {/* Add-on Notice */}
          {isAddon && (
            <div className="mb-8">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-slate-300">
                  💡 <strong>Add-ons are crypto-only purchases.</strong> Pay with USDC on Polygon network and save 8%.
                </p>
              </div>
            </div>
          )}

          {/* PayPal Payment */}
          {paymentMethod === 'paypal' && !isAddon && (
            <div className="space-y-4">
              {PAYPAL_BUTTONS[serviceId] ? (
                <>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-sm text-slate-300">
                      Click the button below to complete your payment securely through PayPal.
                    </p>
                  </div>
                  
                  {paypalLoaded ? (
                    <div className="bg-slate-900/50 rounded-lg p-6">
                      <PayPalButton buttonId={PAYPAL_BUTTONS[serviceId]} renderDelay={0} />
                    </div>
                  ) : (
                    <div className="bg-slate-900/50 rounded-lg p-6 text-center">
                      <p className="text-slate-400">Loading PayPal...</p>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500 text-center">
                    Secure payment powered by PayPal
                  </p>
                </>
              ) : (
                <div className="bg-slate-900/50 rounded-lg p-6 text-center">
                  <p className="text-slate-400">
                    PayPal payment not configured for this service. Please use crypto payment.
                  </p>
                  <Button
                    onClick={() => setPaymentMethod('crypto')}
                    className="mt-4 gradient-accent"
                  >
                    Switch to Crypto Payment
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Crypto Payment */}
          {paymentMethod === 'crypto' && (
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Pay with USDC on Polygon
                </h3>
                <p className="text-sm text-slate-300 mb-4">
                  Send exactly <span className="font-bold text-white">{displayPrice} USDC</span> to the address below using Metamask or any Web3 wallet.
                </p>
              </div>

              {/* Platform Wallet Address */}
              <div>
                <Label className="mb-2 block">Send USDC to this address:</Label>
                <div className="flex gap-2">
                  <Input
                    value={PLATFORM_WALLET}
                    readOnly
                    className="font-mono text-sm bg-slate-900"
                  />
                  <Button
                    onClick={() => copyToClipboard(PLATFORM_WALLET)}
                    variant="outline"
                    size="icon"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Network: Polygon • Token: USDC ({USDC_POLYGON})
                </p>
              </div>

              {/* Instructions */}
              <div className="bg-slate-900/50 rounded-lg p-4 text-sm space-y-2">
                <p className="font-semibold mb-2">📋 Payment Instructions:</p>
                <ol className="list-decimal list-inside space-y-1 text-slate-300">
                  <li>Open Metamask and switch to Polygon network</li>
                  <li>Send exactly <strong>{displayPrice} USDC</strong> to the address above</li>
                  <li>Wait for transaction confirmation</li>
                  <li>Copy your transaction hash and paste it below</li>
                  <li>We'll verify and activate your service within 1 hour</li>
                </ol>
              </div>

              {/* Submit Transaction Form */}
              <form onSubmit={handleCryptoSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="wallet">Your Wallet Address (optional)</Label>
                  <Input
                    id="wallet"
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder="0x..."
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="txHash">Transaction Hash *</Label>
                  <Input
                    id="txHash"
                    value={txHash}
                    onChange={(e) => setTxHash(e.target.value)}
                    placeholder="0x..."
                    required
                    className="font-mono"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Find this in Metamask after your transaction confirms
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !txHash}
                  className="w-full gradient-accent hover:opacity-90 h-12 text-lg"
                >
                  {loading ? 'Submitting...' : 'Submit Payment for Verification'}
                </Button>
              </form>

              <div className="flex items-center gap-2 text-sm text-slate-400">
                <ExternalLink className="w-4 h-4" />
                <a
                  href={`https://polygonscan.com/address/${PLATFORM_WALLET}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-400 underline"
                >
                  View wallet on PolygonScan
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/pricing')}
            className="text-slate-400 hover:text-white transition-colors"
          >
            ← Back to Services
          </button>
        </div>
      </div>
    </div>
  );
}
